import { Component, ChangeDetectionStrategy, Input, EventEmitter, Output } from "@angular/core";
import { Router } from "@angular/router";
import { ActivatedRoute } from "@angular/router";
import { MAT_DATE_FORMATS } from "@angular/material/core";
import { UntypedFormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { DateTime } from "luxon";
import { AuthService } from "../../service/auth.service";
import { AnalyticsService } from "../../service/analytics.service";
import { GeneralService } from "../../service/general.service";
import { ValidationService } from "../../service/validation.service";
import { Account } from "../../entity/Account";
import { ExistingContract } from "../../entity/ExistingContract";
import { User } from "../../entity/User";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { ConfirmationDialog } from "./confirmation-dialog.component";
import { ContractStatus } from "../../obj/ContractStatus";
import { LoaderService } from "../../service/loader.service";
import { CurrencyPipe, DatePipe } from "@angular/common";
import { MatDatepickerInput, MatDatepicker } from "@angular/material/datepicker";
import { OneClickDirective } from "../helpers/one-click.component";
import { AbstractComponent } from "../abstract/abstract.component";
import { NgxMaskDirective } from "ngx-mask";

export const MY_FORMATS = {
    parse: {
        dateInput: "MM/dd/yyyy",
    },
    display: {
        dateInput: "MM/dd/yyyy",
        monthYearLabel: "MMM yyyy",
        dateA11yLabel: "LL",
        monthYearA11yLabel: "MMMM yyyy",
    },
};

@Component({
    selector: "kilowant-account-add-step3",
    templateUrl: "../../../html/incs/account-add-step3.html",
    providers: [
        { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS }
    ],
    imports: [FormsModule, ReactiveFormsModule, MatDatepickerInput, MatDatepicker, OneClickDirective, CurrencyPipe, DatePipe, NgxMaskDirective],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AccountAddStep3Component extends AbstractComponent {
    @Input("user") toBeUsedUser: User | null = null;
    @Input("byAdvisor") byAdvisor: boolean = false;

    account: Account;
    @Output("accountChange") accountEmitter: EventEmitter<Account> = new EventEmitter<Account>();
    @Input("account") set setAccountValue(value: Account) {
        this.account = value;
    }

    existingContractId: number | null;
    @Output("existingContractIdChanged") existingContractIdAction: EventEmitter<number | null> = new EventEmitter<number | null>();

    existingContract: ExistingContract|null;
    @Output("existingContractChanged") existingContractAction: EventEmitter<ExistingContract | null> = new EventEmitter<ExistingContract | null>();

    @Output("return") returnAction = new EventEmitter<boolean>();

    globalStart: DateTime;
    globalEnd: DateTime;
    selectCompany: boolean = false;
    addCompanyName: boolean = false;

    dialogRef: MatDialogRef<ConfirmationDialog> = null!;
    existingContracts: ExistingContract[] = [];
    confirmButtonActive: boolean = true;

    utilityRate: number | null = null;
    communityRate: number | null = null;
    setStartingContract: number | null = null;

    termFrom = new UntypedFormControl();
    termTo = new UntypedFormControl();

    constructor(
        router: Router,
        route: ActivatedRoute,
        protected loaderService: LoaderService,
        analyticsService: AnalyticsService,
        generalService: GeneralService,
        public validationService: ValidationService,
        dialog: MatDialog
    ) {
        super(router, route, dialog, analyticsService, generalService);
    }

    ngOnInit(): void {
        super.ngOnInit();
    }

    pageStart(): void {
        this.globalStart = this.currentDate.minus({ years: 5 });
        this.globalEnd = this.currentDate.plus({ years: 5 });

        if (this.toBeUsedUser == null) {
            this.existingContractId = 0;
            this.existingContractIdAction.next(this.existingContractId);
            this.refreshView();
            return;
        }
        var p1 = this.generalService.getUtilityCosts(this.account.utility!, this.account.rateClass!, this.account.loadZone!, this.account.state!, this.account.city!);
        var p2 = this.generalService.getExistingContracts(null);

        Promise.all([p1, p2]).then(x => {
            this.utilityRate = x[0].utilityAvg;
            this.communityRate = x[0].communityAvg;
            this.existingContracts = [];
            for (let i = 0; i < x[1].length; i++) {
                if (x[1][i].status == ContractStatus.PENDING || x[1][i].status == ContractStatus.PENDING_NON_VERIFIED || x[1][i].status == ContractStatus.OPEN) {
                    this.existingContracts.push(x[1][i]);
                }
            }
            if (this.existingContracts.length == 0) {
                this.existingContractId = 0;
                this.existingContractIdAction.next(this.existingContractId);
            }
            this.refreshView();
        });
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }

    cancel(): void {
        this.returnAction.next(false);
    }

    nextStep(): void {
        this.clearErrors();
        this.account.startingContractConfirmed = true;
        if (this.setStartingContract == null) {
            this.setErrorText("Confirm Supply Service Provider.");
            this.refreshView();
            return;
        }
        if (this.setStartingContract == 1) {
            this.account.isCommunity = true;
        } else if (this.setStartingContract == -1) {
            if (!this.checkContractData()) {
                this.refreshView();
                return;
            }
        }
        this.returnAction.next(true);
    }


    checkContractData(): boolean {
        this.clearErrors();
        if (this.existingContractId != null && this.existingContractId > 0) {
            return true;
        }
        if (this.existingContract == null) {
            return false;
        }
        if (this.existingContract.supplierName == null || this.existingContract.supplierName.length == 0) {
            this.addError("currentSupplierName");
        }
        if (
            this.existingContract.price == null || this.existingContract.price == 0
        ) {
            this.addError("currentSupplierPrice");
        }
        if (this.termFrom.value == null || this.termFrom.value.length == 0) {
            this.addError("termFrom");
        }
        if (this.termTo.value == null || this.termTo.value.length == 0) {
            this.addError("termTo");
        }
        if (this.errorY().showError()) {
            this.setErrorText("Please enter the data requested above in order to add your account.");
            return false;
        }
        if (this.termFrom.value >= this.termTo.value) {
            this.addError("termFrom");
            this.addError("termTo");
            this.setErrorText("The ending price plan date shall be ahead of the starting price plan date.");
            return false;
        }
        if (!this.validationService.validate("ExistingContract", "supplierName", this.existingContract.supplierName, this.errorY)) {
            return false;
        }
        this.existingContract.termFrom = this.termFrom.value.toJSDate();
        this.existingContract.termTo = this.termTo.value.toJSDate();
        this.existingContractAction.next(this.existingContract);
        return true;
    }

    inactiveDatesFilter(d: DateTime | null): boolean {
        if (d == null || this.globalStart == null || this.globalEnd == null) {
            return false;
        }
        return (d > this.globalStart) && (d < this.globalEnd);
    }

    setContractType(setStartingContract: number, existingContractId: number): void {
        this.errorY().clear();
        if (existingContractId == -1) {
            this.existingContract = new ExistingContract();
        } else {
            this.existingContract = null;
        }
        this.setStartingContract = setStartingContract;
        this.existingContractId = existingContractId;
        this.existingContractIdAction.next(this.existingContractId);
        this.existingContractAction.next(this.existingContract);
        this.refreshView();
    }

    inactiveDatesFilterTermTo(d: DateTime | null): boolean {
        if (d == null) {
            return false;
        }
        let from;
        if (this.termFrom == null || this.termFrom.value == null) {
            from = this.globalStart;
        } else {
            from = this.termFrom.value;
        }
        return (d > from) && d < this.globalEnd;
    }

    boundInactiveDatesFilter = this.inactiveDatesFilter.bind(this);
    boundInactiveDatesFilterTermTo = this.inactiveDatesFilterTermTo.bind(this);
}
